import { apiList, apiCreate, apiUpdate } from './apiClient';
import { STRIPE_PROXY_URL } from '../config/billing';
import { stripeFetch } from './stripe';

export interface BillingProduct {
  id: number;
  name: string;
  description: string;
  service_key: string;
  category: string;
  default_price_cents: number;
  billing_type: 'recurring' | 'metered' | 'one_time';
  stripe_product_id?: string;
  stripe_price_id?: string;
  active: boolean;
  created_at?: string;
}

export async function getProducts(): Promise<BillingProduct[]> {
  try {
    return await apiList('billing_products');
  } catch {
    return [];
  }
}

export async function createProduct(data: Partial<BillingProduct>): Promise<BillingProduct> {
  // Create in Stripe first
  let stripeProduct: any = null;
  let stripePrice: any = null;
  try {
    stripeProduct = await stripeFetch(`${STRIPE_PROXY_URL}/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        metadata: { service_key: data.service_key, category: data.category },
      }),
    }).then(r => r.json());

    if (stripeProduct?.id && data.default_price_cents) {
      stripePrice = await stripeFetch(`${STRIPE_PROXY_URL}/prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product: stripeProduct.id,
          unit_amount: data.default_price_cents,
          currency: 'usd',
          recurring: data.billing_type === 'recurring' ? { interval: 'month' } : undefined,
        }),
      }).then(r => r.json());
    }
  } catch (e) {
    console.error('Stripe product creation failed:', e);
  }

  return apiCreate('billing_products', {
    ...data,
    stripe_product_id: stripeProduct?.id || '',
    stripe_price_id: stripePrice?.id || '',
    active: true,
  });
}

export async function updateProduct(id: number, data: Partial<BillingProduct>): Promise<BillingProduct> {
  // Update in Stripe if we have a product ID
  if (data.stripe_product_id) {
    try {
      await stripeFetch(`${STRIPE_PROXY_URL}/products/${data.stripe_product_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          active: data.active,
        }),
      });
    } catch (e) {
      console.error('Stripe product update failed:', e);
    }
  }
  return apiUpdate('billing_products', id, data);
}

export async function archiveProduct(id: number, stripeProductId?: string): Promise<void> {
  if (stripeProductId) {
    try {
      await stripeFetch(`${STRIPE_PROXY_URL}/products/${stripeProductId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: false }),
      });
    } catch (e) {
      console.error('Stripe product archive failed:', e);
    }
  }
  await apiUpdate('billing_products', id, { active: false });
}

// ── Stripe Products/Prices direct management ──

export async function getStripeProducts(): Promise<any[]> {
  try {
    const res = await stripeFetch(`${STRIPE_PROXY_URL}/products?limit=100`);
    const data = await res.json();
    return Array.isArray(data) ? data : data?.data || [];
  } catch {
    return [];
  }
}

export async function getStripePrices(productId?: string): Promise<any[]> {
  try {
    const params = productId ? `?product=${productId}` : '?limit=100';
    const res = await stripeFetch(`${STRIPE_PROXY_URL}/prices${params}`);
    const data = await res.json();
    return Array.isArray(data) ? data : data?.data || [];
  } catch {
    return [];
  }
}

export async function createStripePrice(productId: string, unitAmount: number, recurring?: { interval: string }): Promise<any> {
  return stripeFetch(`${STRIPE_PROXY_URL}/prices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product: productId, unit_amount: unitAmount, currency: 'usd', recurring }),
  }).then(r => r.json());
}

export async function updateStripeProduct(productId: string, data: Record<string, any>): Promise<any> {
  return stripeFetch(`${STRIPE_PROXY_URL}/products/${productId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.json());
}

/* ─── v2 billing-api: editable Products catalog with bidirectional Stripe sync ───
 * These hit the v2 backend (api.intrlock.io/billing-products) which mirrors
 * to Stripe automatically. Use these for new code. The functions above hit
 * the legacy us-east-2 Stripe proxy directly and remain for backward compat.
 */

const V2_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

export interface V2BillableItem {
  id: string;
  dealer_id: string | null;
  name: string;
  description: string | null;
  unit_amount_cents: number;
  currency: string;
  interval: 'day' | 'week' | 'month' | 'year';
  usage_type: 'licensed' | 'metered';
  meter_key: string | null;
  stripe_meter_id: string | null;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  enabled: boolean;
  archived_at: string | null;
}

export interface V2StripeMeter {
  id: string;
  event_name: string;
  display_name: string;
  status: string;
}

function v2Headers(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}`,
  };
}

export async function listV2BillableItems(includeArchived = false): Promise<V2BillableItem[]> {
  const qs = includeArchived ? '?include_archived=true' : '';
  const res = await fetch(`${V2_BASE}/billing-products${qs}`, { headers: v2Headers() });
  if (!res.ok) throw new Error(`listV2BillableItems ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function createV2BillableItem(body: Omit<Partial<V2BillableItem>, 'id'> & { name: string; unit_amount_cents: number }): Promise<V2BillableItem> {
  const res = await fetch(`${V2_BASE}/billing-products`, {
    method: 'POST', headers: v2Headers(), body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail ?? data.error ?? `create ${res.status}`);
  return data;
}

export async function updateV2BillableItem(id: string, body: Partial<V2BillableItem>): Promise<V2BillableItem> {
  const res = await fetch(`${V2_BASE}/billing-products/${id}`, {
    method: 'PUT', headers: v2Headers(), body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail ?? data.error ?? `update ${res.status}`);
  return data;
}

export async function deleteV2BillableItem(id: string): Promise<void> {
  const res = await fetch(`${V2_BASE}/billing-products/${id}`, {
    method: 'DELETE', headers: v2Headers(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? data.error ?? `delete ${res.status}`);
  }
}

export async function listV2StripeMeters(): Promise<V2StripeMeter[]> {
  const res = await fetch(`${V2_BASE}/billing-meters`, { headers: v2Headers() });
  if (!res.ok) throw new Error(`listV2StripeMeters ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}
